import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchAircraftTypes,
  fetchAircraftType,
  fetchAircraftTypeByCode,
  fetchAircraftTypeByIcao,
  createAircraftType,
  updateAircraftType,
  deleteAircraftType,
} from '@/lib/api/aircraft-types'
import type {
  AircraftTypeInsert,
  AircraftTypeUpdate,
  AircraftTypeFilters,
} from '@/types/aircraft-types'
import { toast } from 'sonner'

/**
 * Query key factory for aircraft types
 * Provides consistent keys for cache management
 */
export const aircraftTypeKeys = {
  all: ['aircraftTypes'] as const,
  lists: () => [...aircraftTypeKeys.all, 'list'] as const,
  list: (filters?: AircraftTypeFilters) =>
    [...aircraftTypeKeys.lists(), filters] as const,
  details: () => [...aircraftTypeKeys.all, 'detail'] as const,
  detail: (id: string) => [...aircraftTypeKeys.details(), id] as const,
  byCode: (code: string) => [...aircraftTypeKeys.all, 'byCode', code] as const,
  byIcao: (icao: string) => [...aircraftTypeKeys.all, 'byIcao', icao] as const,
}

/**
 * Fetch all aircraft types with optional filtering
 */
export function useAircraftTypes(filters?: AircraftTypeFilters) {
  return useQuery({
    queryKey: aircraftTypeKeys.list(filters),
    queryFn: () => fetchAircraftTypes(filters),
  })
}

/**
 * Fetch a single aircraft type by ID
 */
export function useAircraftType(id: string | undefined) {
  return useQuery({
    queryKey: aircraftTypeKeys.detail(id || ''),
    queryFn: () => fetchAircraftType(id!),
    enabled: !!id,
  })
}

/**
 * Fetch aircraft type by type_code
 */
export function useAircraftTypeByCode(typeCode: string | undefined) {
  return useQuery({
    queryKey: aircraftTypeKeys.byCode(typeCode || ''),
    queryFn: () => fetchAircraftTypeByCode(typeCode!),
    enabled: !!typeCode,
  })
}

/**
 * Fetch aircraft type by ICAO designator
 */
export function useAircraftTypeByIcao(icaoDesignator: string | undefined) {
  return useQuery({
    queryKey: aircraftTypeKeys.byIcao(icaoDesignator || ''),
    queryFn: () => fetchAircraftTypeByIcao(icaoDesignator!),
    enabled: !!icaoDesignator,
  })
}

/**
 * Create a new aircraft type
 */
export function useCreateAircraftType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AircraftTypeInsert) => createAircraftType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aircraftTypeKeys.lists() })
      toast.success('Aircraft type created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create aircraft type', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing aircraft type
 */
export function useUpdateAircraftType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AircraftTypeUpdate }) =>
      updateAircraftType(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aircraftTypeKeys.lists() })
      queryClient.invalidateQueries({ queryKey: aircraftTypeKeys.detail(id) })
      toast.success('Aircraft type updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update aircraft type', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete an aircraft type
 */
export function useDeleteAircraftType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteAircraftType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aircraftTypeKeys.lists() })
      toast.success('Aircraft type deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete aircraft type', {
        description: error.message,
      })
    },
  })
}
